import {
  AfterViewChecked,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  RendererStyleFlags2,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { AssetsSharedService } from 'shared/services/assets-shared.service';

import { templateUrl, styleUrls } from './render-video.component.paths';


@Component({
  selector: 'render-video',
  templateUrl,
  styleUrls
})
export class RenderVideoComponent implements OnInit, OnChanges, AfterViewChecked {

  @Input() public isActive = true;
  @Input() public ariaLabel!: string;
  @Input() public autoPlay = false;
  @Input() public clickable = true;
  @Input() public clickableWithControls = false;
  @Input() public controls = false;
  @Input() public loop = false;
  @Input() public muted = false;
  @Input() public objectFit!: string;
  @Input() public objectPosition!: string;
  @Input() public poster: LazyDataInterface | undefined;
  @Input() public source!: string;
  @Input() public title!: string;
  @Input() public hudsonStyle?: 'mosaic' | 'profile';

  public isPlaying: boolean = true;
  public isMuted: boolean = true;

  @ViewChild('videoElement')
  private readonly ownVideoElement?: ElementRef<HTMLVideoElement>;

  private hasToWaitForHLS = true;

  constructor(
    private readonly assetsSharedService: AssetsSharedService,
    private readonly element: ElementRef,
    private readonly renderer: Renderer2
  ) { }

  public playVideoWithoutControls(video: HTMLVideoElement): void {
    if ((!this.controls && this.clickable) || (this.clickableWithControls)) {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    }
  }

  public togglePlayPause(videoElement: HTMLVideoElement, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (videoElement.paused) {
      videoElement.play();
    } else {
      videoElement.pause();
    }
  }

  public toggleMute(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (this.ownVideoElement) {
      this.isMuted = !this.isMuted;
      this.ownVideoElement.nativeElement.muted = this.isMuted;
    }
  }

  public ngOnInit(): void {
    this.isPlaying = this.autoPlay;
    this.isMuted = this.muted;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.muted && !changes.muted.isFirstChange() && this.ownVideoElement) {
      this.ownVideoElement.nativeElement.muted = this.muted;
    }

    if (this.objectFit) {
      this.renderer.setStyle(this.element.nativeElement, 'imageObjectFit', `${this.objectFit}`, RendererStyleFlags2.DashCase);
    }

    if (this.objectPosition) {
      this.renderer.setStyle(this.element.nativeElement, 'imageObjectPosition', `${this.objectPosition}`, RendererStyleFlags2.DashCase);
    }
  }

  public async ngAfterViewChecked(): Promise<void> { // eslint-disable-line @typescript-eslint/no-misused-promises
    if (!this.isActive) {
      this.hasToWaitForHLS = true;
    } else {
      if (!this.hasToWaitForHLS || !this.ownVideoElement || !this.source) {
        return;
      }

      this.hasToWaitForHLS = false;
      const videoSource = this.ownVideoElement.nativeElement.src;

      if (this.assetsSharedService.isHLSVideo(videoSource)) {
        await this.initHLS(this.ownVideoElement.nativeElement);
      }
    }
  }

  protected onPlay(): void {
    this.isPlaying = true;
  }

  protected onPause(): void {
    this.isPlaying = false;
  }

  private isSafariBrowser(): boolean {
    const ua = navigator.userAgent;
    // Check for Safari but exclude other browsers
    return /Safari/.test(ua)
      && !/Chrome/.test(ua) // Chrome (desktop + Android WebView base)
      && !/Chromium/.test(ua) // Chromium-based browsers
      && !/Firefox/.test(ua) // Firefox
      && !/Edg/.test(ua) // Edge (Chromium): "Edg/" en desktop, "EdgA" en Android, "EdgiOS" en iOS
      && !/OPR/.test(ua); // Opera
  }

  private async initHLS(video: HTMLVideoElement): Promise<void> {
    // Safari has native support for HLS, so we can just use the native video element to play the video.
    if (this.isSafariBrowser() && video.canPlayType('application/vnd.apple.mpegurl')) {
      video.addEventListener('canplay', () => {
        video.play();
      }, { once: true });
      return;
    }

    const Hls = (await import('hls.js')).default;
    if (Hls.isSupported()) {
      const hls = new Hls({
        // Add here any required parameters for advanced tuning.
        // Check https://github.com/video-dev/hls.js/blob/master/docs/API.md
        capLevelToPlayerSize: true
      });
      hls.loadSource(video.src);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (event: string, data: { fatal: boolean; type: string }) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR: // eslint-disable-line @typescript-eslint/no-unsafe-enum-comparison
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR: // eslint-disable-line @typescript-eslint/no-unsafe-enum-comparison
              hls.recoverMediaError();
              break;
            default:
              // Cannot recover
              hls.destroy();
              break;
          }
        }
      });
    }
  }

}
